/**
 * Mock data for T5 Eingang/Kommunikation — ported from v0 brokly-data.ts / kommunikation-view.tsx.
 * Same kundeId values as T1 screens for cross-screen consistency.
 * FRONTEND-ONLY: no real API wiring this pass.
 */
import { Mail, Globe, Phone, FileText, Upload } from 'lucide-react';
import type {
  EingangMail,
  AusgangMail,
  KundeOption,
  Triage,
  Kanal,
  OutStatus,
  BadgeTone,
  Aufgabenstatus,
} from './types';

/* ── Kanal-Metadaten ── */
export const KANAL_META: Record<Kanal, { label: string; icon: typeof Mail }> = {
  email: { label: 'E-Mail', icon: Mail },
  portal: { label: 'Portal', icon: Globe },
  telefon: { label: 'Telefon', icon: Phone },
  brief: { label: 'Brief', icon: FileText },
  upload: { label: 'Upload', icon: Upload },
  scanning_center: { label: 'Scan-Center', icon: FileText },
};

/* ── Triage-Metadaten ── */
export const TRIAGE_META: Record<Triage, { label: string; tone: BadgeTone }> = {
  neu: { label: 'Neu', tone: 'decide' },
  bearbeitung: { label: 'In Bearbeitung', tone: 'info' },
  beantwortet: { label: 'Beantwortet', tone: 'ok' },
};

/* ── Aufgabenstatus-Metadaten (inline row status — PART A3) ── */
export const AUFGABENSTATUS_META: Record<Aufgabenstatus, { label: string; tone: BadgeTone }> = {
  offen: { label: 'Offen', tone: 'decide' },
  in_bearbeitung: { label: 'In Bearbeitung', tone: 'info' },
  wartend: { label: 'Wartend', tone: 'decide' },
  erledigt: { label: 'Erledigt', tone: 'ok' },
  keine: { label: 'Keine Aufgabe', tone: 'neutral' },
};

/* ── Out-Status-Metadaten ── */
export const OUT_STATUS: Record<OutStatus, { label: string; tone: BadgeTone }> = {
  entwurf: { label: 'Entwurf', tone: 'neutral' },
  versandbereit: { label: 'Versandbereit', tone: 'info' },
  geplant: { label: 'Geplant', tone: 'decide' },
  fehler: { label: 'Fehler', tone: 'alert' },
  gesendet: { label: 'Gesendet', tone: 'ok' },
};

/* ── Kunden-Auswahl für Inline-Zuordnung ── */
export const KUNDEN_OPTIONS: KundeOption[] = [
  { id: 'k-alpina', name: 'Alpina Pharma AG' },
  { id: 'k-bergland', name: 'Bergland Logistik AG' },
  { id: 'k-basler', name: 'Basler Chemie AG' },
  { id: 'k-geneve', name: 'Genève Maritime SA' },
  { id: 'k-zpaeg', name: 'Zürcher Präzision AG' },
  { id: 'k-tessin', name: 'Tessin Wein AG' },
];

/* ── Eingang (5 Nachrichten) ── */
export const EINGANG: EingangMail[] = [
  {
    id: 'IN-1',
    absender: 'schaden@axa.ch',
    absenderRolle: 'AXA · Schadenabteilung',
    betreff: 'Schadenmeldung Wasserschaden: Police POL-883-4471',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    kundeMatched: true,
    kategorie: 'Schaden',
    kanal: 'email',
    zeit: 'vor 12 Min.',
    datum: '18.06.2026, 08:42',
    triage: 'neu',
    konfidenz: 88,
    text: 'Guten Tag, wir bestätigen den Eingang der Schadenmeldung vom 14.06. Für die weitere Bearbeitung benötigen wir das ausgefüllte Schadenprotokoll sowie Fotos des betroffenen Lagerbereichs. Bitte um Zustellung bis spätestens 20.06.',
    kiZusammenfassung:
      'Die AXA fordert für den gemeldeten Wasserschaden (POL-883-4471) das Schadenprotokoll und Fotos bis 20.06. an. Kein Entscheid nötig, die Unterlagen liegen im Kundendossier bereits vor.',
    kiAntwort:
      'Sehr geehrte Damen und Herren,\n\nbesten Dank für Ihre Rückmeldung. Im Anhang finden Sie das vollständige Schadenprotokoll sowie die Fotodokumentation des Lagerbereichs.\n\nFreundliche Grüsse\nLara Meier, Brokly',
    felder: [
      { label: 'Police', value: 'POL-883-4471' },
      { label: 'Schadenart', value: 'Wasserschaden' },
      { label: 'Frist', value: '20.06.2026' },
      { label: 'Schadendatum', value: '14.06.2026' },
    ],
    anhaenge: [{ name: 'Eingangsbestätigung_AXA.pdf', size: '184 KB' }],
    pipeline: [
      { label: 'Eingegangen & klassifiziert', status: 'done' },
      { label: 'Kunde zugeordnet', status: 'done' },
      { label: 'Unterlagen zusammenstellen', status: 'current' },
      { label: 'Antwort versenden', status: 'todo' },
    ],
    verlauf: [
      { time: '08:42', text: 'Nachricht eingegangen', actor: 'person' },
      { time: '08:42', text: 'KI hat als «Schaden» klassifiziert', actor: 'ki' },
      { time: '08:43', text: 'KI hat Kunde «Bergland Logistik AG» zugeordnet', actor: 'ki' },
    ],
    tags: ['Schaden', 'Frist 20.06'],
    notiz: 'Fotos liegen im Dossier unter /Bergland/Schaden-2026.',
  },
  {
    id: 'IN-2',
    absender: 'r.huber@bergland-logistik.ch',
    absenderRolle: 'Roman Huber · Geschäftsführer',
    betreff: 'Frage zur Deckung von Mitarbeiterfahrzeugen',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    kundeMatched: true,
    kategorie: 'Anfrage',
    kanal: 'email',
    zeit: 'vor 41 Min.',
    datum: '18.06.2026, 08:13',
    triage: 'neu',
    konfidenz: 73,
    text: 'Hallo Frau Meier, wir stellen drei neue Lieferwagen in Betrieb. Sind diese über unsere bestehende Flottenpolice automatisch gedeckt oder müssen wir sie separat melden?',
    kiZusammenfassung:
      'Kunde fragt, ob drei neue Lieferwagen über die bestehende Flottenpolice gedeckt sind. Laut Police ist eine Meldung innert 30 Tagen erforderlich. Der Vorschlag enthält den Hinweis.',
    kiAntwort:
      'Guten Tag Herr Huber,\n\ngerne bestätige ich: Neue Fahrzeuge sind über Ihre Flottenpolice gedeckt, müssen aber innert 30 Tagen gemeldet werden. Senden Sie mir bitte Fahrzeugausweise und Inbetriebnahmedaten, ich übernehme die Meldung.\n\nFreundliche Grüsse\nLara Meier',
    felder: [
      { label: 'Police', value: 'Flottenpolice POL-771-2210' },
      { label: 'Anzahl Fahrzeuge', value: '3' },
      { label: 'Meldefrist', value: '30 Tage' },
    ],
    anhaenge: [],
    pipeline: [
      { label: 'Eingegangen & klassifiziert', status: 'done' },
      { label: 'Kunde zugeordnet', status: 'done' },
      { label: 'Antwort prüfen & freigeben', status: 'current' },
    ],
    verlauf: [
      { time: '08:13', text: 'Nachricht eingegangen', actor: 'person' },
      { time: '08:13', text: 'KI hat als «Anfrage» klassifiziert', actor: 'ki' },
    ],
    tags: ['Flotte', 'Deckung'],
  },
  {
    id: 'IN-3',
    absender: 'renewals@css.ch',
    absenderRolle: 'CSS · Vertragsmanagement',
    betreff: 'Erneuerungsofferte 2027: Basler Chemie AG',
    kunde: 'Basler Chemie AG',
    kundeId: 'k-basler',
    kundeMatched: true,
    kategorie: 'Renewal',
    kanal: 'portal',
    zeit: 'vor 2 Std.',
    datum: '18.06.2026, 06:55',
    triage: 'neu',
    konfidenz: 64,
    text: 'Anbei die Erneuerungsofferte für das Vertragsjahr 2027. Die Prämie steigt um 6.5 % gegenüber dem Vorjahr aufgrund der Schadenquote. Wir bitten um Rückmeldung bis 30.06.',
    kiZusammenfassung:
      'CSS legt die Renewal-Offerte 2027 vor, Prämie +6.5 % wegen Schadenquote. Entscheid nötig: annehmen, verhandeln oder Markt anfragen. Differenz liegt über interner Toleranz.',
    kiAntwort:
      'Guten Tag\n\nbesten Dank für die Offerte. Aufgrund der Erhöhung um 6.5 % möchten wir vor einer Zusage das Schadenbild gemeinsam besprechen. Hätten Sie diese Woche Zeit für einen kurzen Termin?\n\nFreundliche Grüsse\nLara Meier',
    felder: [
      { label: 'Vertragsjahr', value: '2027' },
      { label: 'Prämienänderung', value: '+6.5 %' },
      { label: 'Frist', value: '30.06.2026' },
      { label: 'Grund', value: 'Schadenquote' },
    ],
    anhaenge: [
      { name: 'Offerte_2027_BaslerChemie.pdf', size: '412 KB' },
      { name: 'Schadenstatistik_2024-2026.xlsx', size: '96 KB' },
    ],
    pipeline: [
      { label: 'Eingegangen & klassifiziert', status: 'done' },
      { label: 'Kunde zugeordnet', status: 'done' },
      { label: 'Entscheid: annehmen / verhandeln', status: 'current' },
      { label: 'Rückmeldung an CSS', status: 'todo' },
    ],
    verlauf: [
      { time: '06:55', text: 'Offerte über CSS-Portal eingegangen', actor: 'person' },
      { time: '06:55', text: 'KI hat als «Renewal» klassifiziert', actor: 'ki' },
      { time: '06:56', text: 'KI markiert: Prämie über Toleranz, Entscheid nötig', actor: 'ki' },
    ],
    tags: ['Renewal 2027', 'Entscheid'],
    notiz: 'Vor Zusage Schadenbild mit Kunden besprechen.',
  },
  {
    id: 'IN-4',
    absender: 'info@genevemaritime.ch',
    absenderRolle: 'Sophie Rochat · Finanzen',
    betreff: 'Korrektur Rechnungsadresse',
    kunde: 'Genève Maritime SA',
    kundeId: 'k-geneve',
    kundeMatched: true,
    kategorie: 'Stammdaten',
    kanal: 'email',
    zeit: 'vor 5 Std.',
    datum: '18.06.2026, 03:30',
    triage: 'neu',
    konfidenz: 95,
    text: "Bonjour, notre adresse de facturation a changé. Merci de mettre à jour: Genève Maritime SA, Quai du Rhône 12, 1204 Genève.",
    kiZusammenfassung:
      'Kundin meldet neue Rechnungsadresse (Quai du Rhône 12, 1204 Genève). Hohe Konfidenz: Stammdaten-Update kann direkt übernommen werden.',
    kiAntwort:
      'Bonjour Madame Rochat,\n\nmerci pour l\'information. Nous avons mis à jour votre adresse de facturation dans nos systèmes.\n\nMeilleures salutations\nLara Meier',
    felder: [
      { label: 'Feld', value: 'Rechnungsadresse' },
      { label: 'Neu', value: 'Quai du Rhône 12, 1204 Genève' },
    ],
    anhaenge: [],
    pipeline: [
      { label: 'Eingegangen & klassifiziert', status: 'done' },
      { label: 'Kunde zugeordnet', status: 'done' },
      { label: 'Stammdaten aktualisieren', status: 'current' },
    ],
    verlauf: [
      { time: '03:30', text: 'Nachricht eingegangen', actor: 'person' },
      { time: '03:30', text: 'KI hat als «Stammdaten» klassifiziert', actor: 'ki' },
    ],
    tags: ['Stammdaten'],
  },
  {
    id: 'IN-5',
    absender: 'team@helvetia.ch',
    absenderRolle: 'Helvetia · Underwriting',
    betreff: 'Police POL-551-9023: fehlende Unterschrift',
    kunde: '-',
    kundeId: '',
    kundeMatched: false,
    kategorie: 'Police',
    kanal: 'brief',
    zeit: 'gestern',
    datum: '17.06.2026, 16:20',
    triage: 'bearbeitung',
    konfidenz: 81,
    text: 'Für die Aktivierung der Police POL-551-9023 fehlt uns die unterzeichnete Antragsseite 4. Bitte nachreichen.',
    kiZusammenfassung:
      'Helvetia benötigt die unterschriebene Seite 4 zur Police-Aktivierung. Kein eindeutiger Kunden-Match: Police POL-551-9023 manuell zuordnen.',
    kiAntwort:
      'Guten Tag\n\nwir holen die fehlende Unterschrift beim Kunden ein und reichen Seite 4 umgehend nach.\n\nFreundliche Grüsse\nLara Meier',
    felder: [
      { label: 'Police', value: 'POL-551-9023' },
      { label: 'Fehlend', value: 'Unterschrift Seite 4' },
    ],
    anhaenge: [{ name: 'Antrag_POL-551-9023.pdf', size: '1.2 MB' }],
    pipeline: [
      { label: 'Eingegangen & klassifiziert', status: 'done' },
      { label: 'Kunde zuordnen', status: 'current' },
      { label: 'Unterschrift einholen', status: 'todo' },
    ],
    verlauf: [
      { time: '16:20', text: 'Brief gescannt & eingelesen', actor: 'person' },
      { time: '16:21', text: 'KI hat als «Police» klassifiziert', actor: 'ki' },
      { time: '16:21', text: 'KI konnte keinen eindeutigen Kunden zuordnen', actor: 'ki' },
    ],
    tags: ['Police', 'Zuordnen'],
  },
  {
    // The task-free case (B5): no linked task, so no KI decision. Field-for-field what
    // mapCommToEingangMail emits for such a comm.
    id: 'IN-6',
    absender: 's.brunner@brokly.ch',
    absenderRolle: '',
    betreff: 'Interne Notiz: Teamsitzung Donnerstag verschoben',
    kunde: '',
    kundeId: '',
    kundeMatched: true,
    kategorie: 'Intern',
    hasTask: false,
    aufgabenstatus: 'keine',
    kanal: 'email',
    zeit: 'vor 2 Std.',
    datum: '18.06.2026, 06:45',
    triage: 'bearbeitung',
    konfidenz: 100,
    text: 'Die Teamsitzung von Mittwoch verschiebt sich auf Donnerstag 09:00, Sitzungszimmer 2.',
    kiZusammenfassung: '',
    kiAntwort: '',
    felder: [],
    anhaenge: [],
    pipeline: [],
    verlauf: [],
    tags: [],
  },
];

/* ── Ausgehend (8 Nachrichten) ── */
export const AUSGEHEND: AusgangMail[] = [
  {
    id: 'out-1',
    betreff: 'Schadenprotokoll Wasserschaden: Anhang',
    empfaenger: 'schaden@axa.ch',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    zeit: 'vor 8 Min.',
    herkunft: 'ki',
    status: 'versandbereit',
  },
  {
    id: 'out-2',
    betreff: 'Deckungsbestätigung neue Lieferwagen',
    empfaenger: 'r.huber@bergland-logistik.ch',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    zeit: 'vor 30 Min.',
    herkunft: 'ki',
    status: 'entwurf',
  },
  {
    id: 'out-3',
    betreff: 'Erinnerung: Renewal-Termin Basler Chemie',
    empfaenger: 'renewals@css.ch',
    kunde: 'Basler Chemie AG',
    kundeId: 'k-basler',
    zeit: 'heute 14:00',
    herkunft: 'manuell',
    status: 'geplant',
  },
  {
    id: 'out-4',
    betreff: 'Bestätigung Adressänderung',
    empfaenger: 'info@genevemaritime.ch',
    kunde: 'Genève Maritime SA',
    kundeId: 'k-geneve',
    zeit: 'vor 1 Std.',
    herkunft: 'ki',
    status: 'fehler',
    fehlerGrund: 'Empfänger-Server hat die Zustellung abgelehnt (SMTP 550).',
  },
  {
    id: 'out-5',
    betreff: 'Offertanfrage Betriebshaftpflicht',
    empfaenger: 'underwriting@mobiliar.ch',
    kunde: 'Zürcher Präzision AG',
    kundeId: 'k-zpaeg',
    zeit: 'vor 3 Std.',
    herkunft: 'manuell',
    status: 'fehler',
    fehlerGrund: 'Anhang überschreitet die maximale Grösse von 10 MB.',
  },
  {
    id: 'out-6',
    betreff: 'Police POL-551-9023: Unterschrift angefordert',
    empfaenger: 'team@helvetia.ch',
    kunde: 'Alpina Pharma AG',
    kundeId: 'k-alpina',
    zeit: 'gestern',
    herkunft: 'ki',
    status: 'gesendet',
  },
  {
    id: 'out-7',
    betreff: 'Jahresübersicht Prämien 2026',
    empfaenger: 'r.huber@bergland-logistik.ch',
    kunde: 'Bergland Logistik AG',
    kundeId: 'k-bergland',
    zeit: 'vor 2 Tagen',
    herkunft: 'manuell',
    status: 'gesendet',
  },
  {
    id: 'out-8',
    betreff: 'Bestätigung Vertragsanpassung',
    empfaenger: 'info@tessinwein.ch',
    kunde: 'Tessin Wein AG',
    kundeId: 'k-tessin',
    zeit: 'vor 3 Tagen',
    herkunft: 'ki',
    status: 'gesendet',
  },
];

/* ── Saved-View-Definitionen ── */
export type SavedViewKey = 'alle' | 'neu' | 'bearbeitung' | 'beantwortet';

export const EINGANG_VIEWS: { key: SavedViewKey; label: string; triage?: 'neu' | 'bearbeitung' | 'beantwortet' }[] = [
  { key: 'alle', label: 'Alle' },
  { key: 'neu', label: 'Braucht Entscheid', triage: 'neu' },
  { key: 'bearbeitung', label: 'In Bearbeitung', triage: 'bearbeitung' },
  { key: 'beantwortet', label: 'Erledigt', triage: 'beantwortet' },
];
